import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import moment from 'moment';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportLicense,
  ExportProduct,
  ChecklistTemplate,
  ExportLicenseStatus,
  ExportLicenseValueBasis,
  LicenseDocument,
  LicenseDrawdown,
  DrawdownSummary,
} from 'api/ExportHubAPI';
import FacetSelect from './FacetSelect';
import CreatableSelect from './CreatableSelect';
import LicenseRequestWizard from './LicenseRequestWizard';
import ManageTemplatesDrawer from './ManageTemplatesDrawer';
import LinkProductModal from './LinkProductModal';
import DrawdownLedger from './DrawdownLedger';
import RecordDrawdownModal from './RecordDrawdownModal';
import {
  LICENSE_TYPES,
  AGENCIES,
  LICENSE_STATUSES,
  LicenseStatusPill,
  formatCurrency,
} from './licensePresets';
import StyledSingleSelect from './StyledSingleSelect';
import { USML_CATEGORIES } from './jurisdictionFields';

interface Props {
  databaseId: string;
  groupId: string | undefined;
  reloadKey?: number;
  onChanged?: () => void;
}

const dateRange = (l: ExportLicense): string => {
  if (!l.effective_date && !l.expiry_date) return 'Pending';
  const s = l.effective_date
    ? moment(l.effective_date).format('MM/DD/YYYY')
    : '—';
  const e = l.expiry_date ? moment(l.expiry_date).format('MM/DD/YYYY') : '—';
  return `${s} → ${e}`;
};

/**
 * What a licence is classified as, in list form. The line items are the
 * authority — a licence may span several USML categories — so they win over the
 * licence-level default; ECCNs are the EAR fallback. Categories are shortened
 * to their numeral ("VIII — Aircraft …" → "VIII") to keep the column readable.
 */
const classificationLabel = (l: ExportLicense): string => {
  const lines = l.line_usml_categories ?? [];
  if (lines.length > 0) {
    return lines.map((c) => c.split('—')[0].trim()).join(', ');
  }
  if (l.usml_category) return l.usml_category.split('—')[0].trim();
  return l.eccns.length ? l.eccns.join(', ') : '—';
};

const rd = (l: ExportLicense, key: string): string => {
  const v = (l.request_details || {})[key];
  return v === null || v === undefined || v === '' ? '—' : String(v);
};

// Date field styled like the Tariff tool's date pickers (MUI X DatePicker with
// the calendar popup + TODAY action), sized to fit a license detail row.
const DATE_FIELD_SX = {
  width: 180,
  '& .MuiInputBase-root': {
    backgroundColor: 'var(--input-color)',
    borderRadius: '8px',
    height: '40px',
    paddingRight: '8px',
    color: 'var(--input-text-color)',
    fontFamily: 'Inter, sans-serif',
  },
  '& input': {
    color: 'var(--input-text-color)',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 10px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--secondary-color)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--input-placeholder-color)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--action-color)',
  },
  '& .MuiSvgIcon-root': { color: 'var(--focused-text-color)' },
};

const LicenseDateField: React.FC<{
  value: Date | null;
  onChange: (_d: Date | null) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => (
  <DatePicker<Date>
    value={value}
    onChange={onChange}
    slots={{ actionBar: PickersActionBar, textField: TextField }}
    slotProps={{
      actionBar: { actions: ['today'] },
      textField: {
        placeholder: placeholder,
        size: 'small',
        sx: DATE_FIELD_SX,
      },
    }}
  />
);

const ExpandedLicense: React.FC<{
  license: ExportLicense;
  groupId: string;
  templates: ChecklistTemplate[];
  onPatched: (_l: ExportLicense) => void;
  onDeleted: (_id: string) => void;
  onTemplatesChanged: () => void;
}> = ({ license, groupId, templates, onPatched, onDeleted, onTemplatesChanged }) => {
  const [newItem, setNewItem] = useState('');
  const [naming, setNaming] = useState(false);
  const [tplName, setTplName] = useState('');
  const [appliedTplId, setAppliedTplId] = useState<string | null>(null);
  const [linkedProducts, setLinkedProducts] = useState<ExportProduct[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  // Editable license number (saved on blur/Enter).
  const [licNum, setLicNum] = useState(license.license_number || '');
  // Per-ECCN authorized draft inputs, keyed by `${eccn}:authorized`. The
  // applied side is derived from the drawdown ledger and is not editable here.
  const [eccnDraft, setEccnDraft] = useState<Record<string, string>>({});
  // Optimistic value-basis while the save is in flight; null = use the row's.
  const [basisDraft, setBasisDraft] =
    useState<ExportLicenseValueBasis | null>(null);
  // Per-product allocation draft inputs, keyed by product id.
  // In-flight edits on the commodity block, keyed `${productId}:${field}`.
  const [lineDraft, setLineDraft] = useState<Record<string, string>>({});
  // The drawdown ledger + its rollups — the source of truth for consumption.
  const [drawdowns, setDrawdowns] = useState<LicenseDrawdown[]>([]);
  const [summary, setSummary] = useState<DrawdownSummary | null>(null);
  const [ddLoading, setDdLoading] = useState(true);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordProductId, setRecordProductId] = useState<string | undefined>();
  // License documents.
  const [docs, setDocs] = useState<LicenseDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadLinkedProducts = useCallback(async () => {
    try {
      setLinkedProducts(
        await ExportHubAPI.getLinkedProducts(license.id, groupId),
      );
    } catch (err) {
      console.error('Failed to load linked products:', err);
    }
  }, [license.id, groupId]);

  const loadDrawdowns = useCallback(async () => {
    setDdLoading(true);
    try {
      const [rows, sum] = await Promise.all([
        ExportHubAPI.getDrawdowns(license.id, groupId),
        ExportHubAPI.getDrawdownSummary(license.id, groupId),
      ]);
      setDrawdowns(rows);
      setSummary(sum);
    } catch (err) {
      console.error('Failed to load drawdowns:', err);
    } finally {
      setDdLoading(false);
    }
  }, [license.id, groupId]);

  /**
   * Re-read the license after a ledger change. Posting or voiding a transaction
   * moves value_used server-side, so the collapsed row and the KPIs above it
   * would otherwise show a stale figure until the next full reload.
   */
  const refreshLicense = useCallback(async () => {
    try {
      const fresh = await ExportHubAPI.getLicense(license.id, groupId);
      if (fresh) onPatched(fresh);
    } catch (err) {
      console.error('Failed to refresh license:', err);
    }
  }, [license.id, groupId, onPatched]);

  const loadDocs = useCallback(async () => {
    try {
      setDocs(await ExportHubAPI.getLicenseDocuments(license.id, groupId));
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  }, [license.id, groupId]);

  useEffect(() => {
    loadLinkedProducts();
    loadDocs();
    loadDrawdowns();
  }, [loadLinkedProducts, loadDocs, loadDrawdowns]);

  // Keep the editable license number in sync when patched elsewhere.
  useEffect(() => {
    setLicNum(license.license_number || '');
  }, [license.license_number]);

  const saveField = async (patch: {
    license_number?: string | null;
    value?: number | null;
    effective_date?: string | null;
    expiry_date?: string | null;
  }) => {
    try {
      const updated = await ExportHubAPI.updateLicense(
        license.id,
        groupId,
        patch,
      );
      onPatched(updated);
    } catch (err) {
      enqueueSnackbar('Failed to save.', { variant: 'error' });
    }
  };

  const saveLicenseNumber = () => {
    const v = licNum.trim();
    if (v === (license.license_number || '')) return;
    saveField({ license_number: v || null });
  };

  // Per-ECCN value tracking lives in approval_details.eccn_values:
  //   { [eccn]: { authorized: number, applied: number } }
  // Total authorized = license.value. `applied` is NOT written to
  // license.value_used any more — consumption is owned by the drawdown ledger,
  // which is now the only writer of that column (Slice 13).
  type EccnVal = { authorized: number; applied: number };
  const eccnValues: Record<string, EccnVal> =
    (license.approval_details?.eccn_values as Record<string, EccnVal>) || {};
  const eccnVal = (eccn: string): EccnVal =>
    eccnValues[eccn] || { authorized: 0, applied: 0 };

  const saveEccn = async (eccn: string, raw: string) => {
    const num = Math.max(0, Number(raw) || 0);
    if (eccnVal(eccn).authorized === num) return;
    const nextValues: Record<string, EccnVal> = { ...eccnValues };
    // Seed any ECCN on the license that has no entry yet.
    license.eccns.forEach((e) => {
      if (!nextValues[e]) nextValues[e] = { authorized: 0, applied: 0 };
    });
    nextValues[eccn] = { ...eccnVal(eccn), authorized: num };
    const totalAuth = license.eccns.reduce(
      (s, e) => s + (nextValues[e]?.authorized || 0),
      0,
    );
    try {
      const updated = await ExportHubAPI.updateLicense(license.id, groupId, {
        approval_details: { ...license.approval_details, eccn_values: nextValues },
        value: totalAuth,
      });
      onPatched(updated);
    } catch (err) {
      enqueueSnackbar('Failed to update ECCN value.', { variant: 'error' });
    }
  };

  const saveDate = (field: 'effective_date' | 'expiry_date', d: Date | null) => {
    const val = d ? moment(d).format('YYYY-MM-DD') : null;
    saveField(
      field === 'effective_date'
        ? { effective_date: val }
        : { expiry_date: val },
    );
  };

  const onUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const next = await ExportHubAPI.uploadLicenseDocument(
        license.id,
        groupId,
        file,
      );
      setDocs(next);
      enqueueSnackbar('Document uploaded.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to upload document.', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const downloadDoc = async (d: LicenseDocument) => {
    try {
      await ExportHubAPI.downloadLicenseDocument(d.id, groupId, d.file_name);
    } catch (err) {
      enqueueSnackbar('Failed to download document.', { variant: 'error' });
    }
  };

  const deleteDoc = async (id: string) => {
    try {
      await ExportHubAPI.deleteLicenseDocument(id, groupId);
      setDocs((ds) => ds.filter((d) => d.id !== id));
    } catch (err) {
      enqueueSnackbar('Failed to delete document.', { variant: 'error' });
    }
  };

  /**
   * Save a product's planned ceiling on this license. This is a budget, not
   * consumption — the ledger owns what was actually drawn.
   */
  const clearDraft = (key: string): void =>
    setLineDraft((d) => {
      const n = { ...d };
      delete n[key];
      return n;
    });

  /**
   * Patch one field on a licence line. allocated_value is required by the
   * endpoint, so an edit to any other field resends the current one unchanged.
   */
  const saveLine = async (
    productId: string,
    patch: {
      allocated_value?: number;
      allocated_quantity?: number | null;
      default_unit_value?: number | null;
      usml_category?: string | null;
      commodity_code?: string | null;
    },
  ): Promise<void> => {
    try {
      // Send only what was edited. Resending allocated_value from `summary`
      // would overwrite the stored allocation with a stale figure — or with 0
      // while the drawdown summary is still loading or has failed.
      await ExportHubAPI.setProductAllocation(license.id, productId, groupId, {
        ...(patch.allocated_value !== undefined
          ? { allocated_value: Math.max(0, patch.allocated_value) }
          : {}),
        ...(patch.allocated_quantity !== undefined
          ? { allocated_quantity: patch.allocated_quantity }
          : {}),
        ...(patch.default_unit_value !== undefined
          ? { default_unit_value: patch.default_unit_value }
          : {}),
        ...(patch.usml_category !== undefined
          ? { usml_category: patch.usml_category }
          : {}),
        ...(patch.commodity_code !== undefined
          ? { commodity_code: patch.commodity_code }
          : {}),
      });
      loadDrawdowns();
      loadLinkedProducts();
    } catch (err) {
      enqueueSnackbar('Failed to save line item.', { variant: 'error' });
    }
  };

  // Stable single-element array for the record modal (a literal would be a new
  // identity on every render).
  const recordLicenses = useMemo(() => [license], [license]);

  const openRecord = (productId?: string) => {
    setRecordProductId(productId);
    setRecordOpen(true);
  };

  const unlinkProduct = async (productId: string) => {
    try {
      await ExportHubAPI.unlinkProductFromLicense(
        license.id,
        productId,
        groupId,
      );
      enqueueSnackbar('Product unlinked.', { variant: 'success' });
      loadLinkedProducts();
    } catch (err) {
      enqueueSnackbar('Failed to unlink product.', { variant: 'error' });
    }
  };

  const persist = async (checklist: ExportLicense['checklist']) => {
    try {
      const updated = await ExportHubAPI.updateLicense(license.id, groupId, {
        checklist: checklist.map((c) => ({
          id: c.id,
          label: c.label,
          done: c.done,
        })),
      });
      onPatched(updated);
    } catch (err) {
      enqueueSnackbar('Failed to update checklist.', { variant: 'error' });
    }
  };

  const toggleItem = (id: string) =>
    persist(
      license.checklist.map((c) => (c.id === id ? { ...c, done: !c.done } : c)),
    );
  const removeItem = (id: string) =>
    persist(license.checklist.filter((c) => c.id !== id));
  const addItem = () => {
    const label = newItem.trim();
    if (!label) return;
    persist([...license.checklist, { id: '', label: label, done: false }]);
    setNewItem('');
  };
  const applyTemplate = (t: ChecklistTemplate) =>
    persist(t.items.map((i) => ({ id: '', label: i.label, done: false })));

  const startNaming = () => {
    setTplName(`${license.license_type} checklist`);
    setNaming(true);
  };

  const saveAsTemplate = async () => {
    const name = tplName.trim();
    if (!name) {
      enqueueSnackbar('Enter a template name.', { variant: 'warning' });
      return;
    }
    try {
      await ExportHubAPI.createChecklistTemplate({
        group_id: groupId,
        name: name,
        items: license.checklist.map((c) => ({ label: c.label })),
      });
      enqueueSnackbar(`Saved “${name}” as a template.`, {
        variant: 'success',
      });
      setNaming(false);
      setTplName('');
      onTemplatesChanged();
    } catch (err) {
      enqueueSnackbar('Failed to save template.', { variant: 'error' });
    }
  };

  const changeStatus = async (status: ExportLicenseStatus) => {
    try {
      const updated = await ExportHubAPI.updateLicense(license.id, groupId, {
        status: status,
      });
      onPatched(updated);
    } catch (err) {
      enqueueSnackbar('Failed to update status.', { variant: 'error' });
    }
  };

  const remove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this license?')) return;
    try {
      await ExportHubAPI.deleteLicense(license.id, groupId);
      enqueueSnackbar('License deleted.', { variant: 'success' });
      onDeleted(license.id);
    } catch (err) {
      enqueueSnackbar('Failed to delete license.', { variant: 'error' });
    }
  };

  // Authorized ceilings are hand-entered from the issued license; everything
  // consumed is derived from the drawdown ledger, which is the only writer of
  // License.value_used. The two never disagree because only one of them writes.
  const totalAuthorized = summary?.authorized_value ?? license.value ?? 0;
  const totalApplied = summary?.posted_value ?? 0;
  const draftValue = summary?.draft_value ?? 0;
  const hasAuthorized = totalAuthorized > 0;
  const valueRemaining = totalAuthorized - totalApplied;
  const consumedPct = hasAuthorized
    ? Math.min(100, Math.round((totalApplied / totalAuthorized) * 100))
    : 0;
  // Drafts are shown on the bar but not counted in it — you can see what's
  // staged without it moving the compliance number.
  const draftPct = hasAuthorized
    ? Math.max(
        0,
        Math.min(100 - consumedPct, Math.round((draftValue / totalAuthorized) * 100)),
      )
    : 0;
  // Which roll-up this license is managed against. Optimistic so the toggle
  // flips immediately; the row reload reconciles it.
  const basis: ExportLicenseValueBasis = basisDraft ?? license.value_basis ?? 'product';
  const setBasis = async (next: ExportLicenseValueBasis): Promise<void> => {
    if (!groupId || next === basis) return;
    setBasisDraft(next);
    try {
      const updated = await ExportHubAPI.updateLicense(license.id, groupId, {
        value_basis: next,
      });
      // Push the saved row up so the list stops disagreeing with the toggle.
      onPatched(updated);
    } catch (err) {
      setBasisDraft(null);
      enqueueSnackbar('Failed to change value tracking.', { variant: 'error' });
    }
  };

  // Linked products with their allocation and what they've drawn. The summary
  // returns this alongside by_eccn, so switching basis needs no refetch.
  const productRollups = summary?.by_product ?? [];

  // Distinct categories across the line items, shortened to the numeral so a
  // multi-category licence reads as "VIII, XI" rather than two full labels.
  // Falls back to the product's own recorded category when the line item has
  // none. Links created before the line seeded usml_category have a null there,
  // so without this fallback a licence whose products all carry a category
  // still shows nothing here — which is what it did.
  const lineCategories = Array.from(
    new Set(
      linkedProducts
        .map((p) => {
          const productCategory =
            typeof p.jurisdiction_details?.usml_category === 'string'
              ? p.jurisdiction_details.usml_category
              : '';
          return (p.line_usml_category || productCategory || '')
            .split('—')[0]
            .trim();
        })
        .filter(Boolean),
    ),
  ).sort();

  // Quantity is capped alongside value, so the block totals both. A null
  // quantity total means no line states one — that's "unspecified", not zero.
  const linesQty = linkedProducts.reduce<number | null>((n, p) => {
    const q = p.allocated_quantity;
    if (q === null || q === undefined) return n;
    return (n ?? 0) + q;
  }, null);
  const linesDrawnQty = linkedProducts.reduce(
    (n, p) =>
      n +
      (summary?.by_product.find((r) => r.product_id === p.id)?.drawn_quantity ??
        0),
    0,
  );

  // Block 13: the combined value of the line item totals, and what those lines
  // have actually drawn.
  const linesTotal = linkedProducts.reduce(
    (n, p) =>
      n +
      (summary?.by_product.find((r) => r.product_id === p.id)
        ?.allocated_value ?? 0),
    0,
  );
  const linesDrawn = linkedProducts.reduce(
    (n, p) =>
      n +
      (summary?.by_product.find((r) => r.product_id === p.id)?.drawn_value ?? 0),
    0,
  );

  const saveUsmlCategory = async (next: string): Promise<void> => {
    if (!groupId || next === (license.usml_category || '')) return;
    try {
      const updated = await ExportHubAPI.updateLicense(license.id, groupId, {
        usml_category: next || null,
      });
      onPatched(updated);
    } catch (err) {
      enqueueSnackbar('Failed to update USML category.', { variant: 'error' });
    }
  };

  const appliedForEccn = (e: string): number =>
    summary?.by_eccn.find((r) => r.eccn === e)?.applied ?? 0;
  // A transaction can be booked without an ECCN (and the migrated opening
  // balance always is), so surface that bucket rather than hiding the money.
  const unassignedApplied =
    summary?.by_eccn.find((r) => r.eccn === 'Unassigned')?.applied ?? 0;

  // DDTC caps quantity as well as value; the add-license modal already stores the
  // authorized quantity, so track the second ceiling alongside the first.
  const authQty = summary?.authorized_quantity ?? null;
  const postedQty = summary?.posted_quantity ?? 0;
  const qtyPct =
    authQty && authQty > 0
      ? Math.min(100, Math.round((postedQty / authQty) * 100))
      : 0;
  // rd() renders a missing value as an em dash; a unit label wants ''.
  const uomRaw = rd(license, 'unit_of_measure');
  const uom = uomRaw === '—' ? '' : uomRaw;
  const effDate = license.effective_date
    ? new Date(license.effective_date)
    : null;
  const expDate = license.expiry_date ? new Date(license.expiry_date) : null;
  const daysRemaining = license.expiry_date
    ? moment(license.expiry_date)
        .startOf('day')
        .diff(moment().startOf('day'), 'days')
    : null;

  return (
    <div className='eh-lic-expanded'>
      <div className='eh-lic-2col'>
        <div className='eh-lic-detail-block'>
          <div className='eh-lic-block-title'>License Details</div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>License #</span>
            <input
              className='eh-input eh-detail-edit'
              value={licNum}
              onChange={(e) => setLicNum(e.target.value)}
              onBlur={saveLicenseNumber}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              placeholder='Add once issued'
            />
          </div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Customer</span>
            <span className='eh-detail-value'>{rd(license, 'customer')}</span>
          </div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Destination</span>
            <span className='eh-detail-value'>
              {rd(license, 'country_of_ultimate_destination')}
            </span>
          </div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Foreign End User</span>
            <span className='eh-detail-value'>
              {rd(license, 'foreign_end_user')}
            </span>
          </div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Program</span>
            <span className='eh-detail-value'>{rd(license, 'program_name')}</span>
          </div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Signatory</span>
            <span className='eh-detail-value'>{rd(license, 'signatory')}</span>
          </div>
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>Agency</span>
            <span className='eh-detail-value'>{license.agency}</span>
          </div>
          {/* The licence's default category — new lines inherit it. A licence
              may span several, so the line items are the authority; this row
              shows what they actually carry when they differ. */}
          <div className='eh-detail-row'>
            <span className='eh-detail-label'>
              USML Category
              <span className='eh-lic-usml-hint'>default for new lines</span>
            </span>
            <span className='eh-detail-value eh-lic-usml'>
              <StyledSingleSelect
                options={[
                  { value: '', label: 'Not applicable' },
                  ...USML_CATEGORIES.map((c) => ({ value: c, label: c })),
                ]}
                value={license.usml_category || ''}
                onChange={saveUsmlCategory}
                inline
              />
              {lineCategories.length > 0 && (
                <span className='eh-lic-usml-lines'>
                  On line items: {lineCategories.join(', ')}
                </span>
              )}
            </span>
          </div>
          <div className='eh-detail-row eh-detail-row-date'>
            <span className='eh-detail-label'>Effective</span>
            <LicenseDateField
              value={effDate}
              onChange={(d) => saveDate('effective_date', d)}
              placeholder='Effective date'
            />
          </div>
          <div className='eh-detail-row eh-detail-row-date'>
            <span className='eh-detail-label'>Expiry</span>
            <LicenseDateField
              value={expDate}
              onChange={(d) => saveDate('expiry_date', d)}
              placeholder='Expiry date'
            />
          </div>

          {/* Value + time + per-ECCN tracking (spec §7). */}
          <div className='eh-val-card'>
            <div className='eh-val-line'>
              <span className='eh-val-label'>Value remaining</span>
              <span className='eh-val-amount'>
                {hasAuthorized
                  ? formatCurrency(valueRemaining)
                  : formatCurrency(license.proposed_value)}
                <span className='eh-val-of'>
                  {hasAuthorized
                    ? ` of ${formatCurrency(totalAuthorized)}`
                    : ' (proposed)'}
                </span>
              </span>
            </div>
            {hasAuthorized && (
              <>
                <div className='eh-val-bar eh-dd-split-bar'>
                  <span
                    className={`eh-val-bar-fill${
                      consumedPct >= 100
                        ? ' eh-dd-fill-over'
                        : consumedPct > 75
                        ? ' eh-dd-fill-warn'
                        : ''
                    }`}
                    style={{ width: `${consumedPct}%` }}
                  />
                  {draftPct > 0 && (
                    <span
                      className='eh-dd-bar-draft'
                      style={{ width: `${draftPct}%` }}
                      title={`${formatCurrency(draftValue)} staged in drafts`}
                    />
                  )}
                </div>
                <div className='eh-val-subline'>
                  {consumedPct}% consumed
                  {draftValue > 0 &&
                    ` · ${formatCurrency(draftValue)} staged in drafts`}
                </div>
              </>
            )}

            {authQty !== null && (
              <div className='eh-val-line eh-val-time'>
                <span className='eh-val-label'>Quantity remaining</span>
                <span className='eh-val-amount'>
                  {authQty - postedQty}
                  <span className='eh-val-of'>
                    {` of ${authQty}${uom ? ` ${uom}` : ''}`}
                  </span>
                </span>
              </div>
            )}
            {authQty !== null && (
              <div className='eh-val-bar eh-val-bar-sm'>
                <span
                  className={`eh-val-bar-fill${
                    qtyPct >= 100
                      ? ' eh-dd-fill-over'
                      : qtyPct > 75
                      ? ' eh-dd-fill-warn'
                      : ''
                  }`}
                  style={{ width: `${qtyPct}%` }}
                />
              </div>
            )}

            <div className='eh-val-line eh-val-time'>
              <span className='eh-val-label'>Time remaining</span>
              <span className='eh-val-amount'>
                {daysRemaining !== null ? `${daysRemaining} days` : '—'}
              </span>
            </div>

            <div className='eh-eccn-head'>
              <span className='eh-eccn-title'>
                Value applied per {basis === 'product' ? 'product' : 'ECCN'}
              </span>
              <span className='eh-seg-row'>
                <button
                  type='button'
                  className={`eh-seg-btn ${basis === 'product' ? 'active' : ''}`}
                  onClick={() => setBasis('product')}
                  title='Track the authorized value against each linked product'
                >
                  Product
                </button>
                <button
                  type='button'
                  className={`eh-seg-btn ${basis === 'eccn' ? 'active' : ''}`}
                  onClick={() => setBasis('eccn')}
                  title='Track the authorized value against each ECCN bucket'
                >
                  ECCN
                </button>
              </span>
            </div>

            {basis === 'product' ? (
              productRollups.length === 0 ? (
                <div className='eh-detail-note'>
                  Link products to this license to track value by product.
                </div>
              ) : (
                productRollups.map((r) => {
                  const pct =
                    r.allocated_value > 0
                      ? Math.min(
                          100,
                          Math.round((r.drawn_value / r.allocated_value) * 100),
                        )
                      : 0;
                  return (
                    <div className='eh-eccn-row' key={r.product_id}>
                      <div className='eh-eccn-top'>
                        <span className='eh-eccn-code'>{r.product_name}</span>
                        <span className='eh-eccn-vals'>
                          <span className='eh-eccn-applied'>
                            {formatCurrency(r.drawn_value)}
                          </span>
                          <span className='eh-eccn-slash'>
                            /{' '}
                            {r.allocated_value > 0
                              ? formatCurrency(r.allocated_value)
                              : '—'}
                          </span>
                        </span>
                      </div>
                      <div className='eh-val-bar eh-val-bar-sm'>
                        <span
                          className={`eh-val-bar-fill${
                            pct >= 100 ? ' eh-dd-fill-over' : ''
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )
            ) : license.eccns.length === 0 ? (
              <div className='eh-detail-note'>
                Add ECCNs to track value by classification.
              </div>
            ) : (
              license.eccns.map((e) => {
                const authorized = eccnVal(e).authorized;
                const applied = appliedForEccn(e);
                const pct =
                  authorized > 0
                    ? Math.min(100, Math.round((applied / authorized) * 100))
                    : 0;
                const authKey = `${e}:authorized`;
                return (
                  <div className='eh-eccn-row' key={e}>
                    <div className='eh-eccn-top'>
                      <span className='eh-eccn-code'>{e}</span>
                      <span className='eh-eccn-vals'>
                        {/* Applied is rolled up from the ledger — read-only. */}
                        <span className='eh-eccn-applied'>
                          {formatCurrency(applied)}
                        </span>
                        <span className='eh-eccn-slash'>/ $</span>
                        <input
                          className='eh-input eh-detail-edit eh-eccn-input'
                          type='number'
                          min={0}
                          value={eccnDraft[authKey] ?? String(authorized || 0)}
                          onChange={(ev) =>
                            setEccnDraft((d) => ({
                              ...d,
                              [authKey]: ev.target.value,
                            }))
                          }
                          onBlur={(ev) => {
                            saveEccn(e, ev.target.value);
                            setEccnDraft((d) => {
                              const n = { ...d };
                              delete n[authKey];
                              return n;
                            });
                          }}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter')
                              (ev.target as HTMLInputElement).blur();
                          }}
                          title='Authorized'
                        />
                      </span>
                    </div>
                    <div className='eh-val-bar eh-val-bar-sm'>
                      <span
                        className={`eh-val-bar-fill${
                          pct >= 100 ? ' eh-dd-fill-over' : ''
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
            {basis === 'eccn' && unassignedApplied !== 0 && (
              <div className='eh-eccn-row'>
                <div className='eh-eccn-top'>
                  <span className='eh-eccn-code eh-dd-unassigned'>
                    Unassigned
                  </span>
                  <span className='eh-eccn-vals'>
                    <span className='eh-eccn-applied'>
                      {formatCurrency(unassignedApplied)}
                    </span>
                  </span>
                </div>
                <div className='eh-val-subline'>
                  Transactions booked without an ECCN, including any opening
                  balance carried over.
                </div>
              </div>
            )}
            <div className='eh-val-subline eh-dd-eccn-help'>
              {basis === 'product'
                ? 'Applied is rolled up from the drawdown ledger; the ceiling is each product’s allocation on this license.'
                : 'Applied is rolled up from the drawdown ledger. Type the authorized figure from the issued license.'}
            </div>
          </div>
        </div>

        <div className='eh-lic-right'>
          <div className='eh-lic-right-grid'>
            <div className='eh-lic-detail-block'>
          <div className='eh-lic-block-title'>
            Checklist ({license.checklist.filter((c) => c.done).length}/
            {license.checklist.length})
          </div>
          <div className='eh-checklist'>
            {license.checklist.map((c) => (
              <div className='eh-checklist-item' key={c.id}>
                <button
                  type='button'
                  className={`eh-check-box ${c.done ? 'on' : ''}`}
                  onClick={() => toggleItem(c.id)}
                  aria-label={c.done ? 'Mark incomplete' : 'Mark complete'}
                />
                <span className={`eh-checklist-label ${c.done ? 'done' : ''}`}>
                  {c.label}
                </span>
                <button
                  type='button'
                  className='eh-checklist-x'
                  onClick={() => removeItem(c.id)}
                  aria-label='Remove item'
                >
                  <CloseIcon style={{ fontSize: 13 }} />
                </button>
              </div>
            ))}
            {license.checklist.length === 0 && (
              <div className='eh-detail-note'>No checklist items yet.</div>
            )}
          </div>
          <div className='eh-checklist-add'>
            <input
              className='eh-input'
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem();
                }
              }}
              placeholder='Add checklist item'
            />
            <button className='btn-outline' type='button' onClick={addItem}>
              Add
            </button>
          </div>
          <div className='eh-checklist-actions'>
            {naming ? (
              <>
                <input
                  className='eh-input'
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      saveAsTemplate();
                    }
                    if (e.key === 'Escape') setNaming(false);
                  }}
                  placeholder='Template name'
                  autoFocus
                />
                <button
                  className='btn-primary'
                  type='button'
                  onClick={saveAsTemplate}
                >
                  Save
                </button>
                <button
                  className='btn-outline'
                  type='button'
                  onClick={() => setNaming(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <FacetSelect
                  label='Apply template'
                  options={templates.map((t) => ({
                    value: t.id,
                    label: t.name,
                  }))}
                  selected={appliedTplId ? [appliedTplId] : []}
                  onChange={(sel) => {
                    const id = sel[sel.length - 1];
                    if (!id) {
                      setAppliedTplId(null);
                      return;
                    }
                    const t = templates.find((x) => x.id === id);
                    if (t) {
                      applyTemplate(t);
                      setAppliedTplId(id);
                    }
                  }}
                />
                <button
                  className='btn-outline'
                  type='button'
                  onClick={startNaming}
                  disabled={license.checklist.length === 0}
                >
                  Save as template
                </button>
              </>
            )}
          </div>
        </div>

        {/* Column 3 — Documents (S3-backed) */}
        <div className='eh-lic-detail-block'>
          <div className='eh-lic-block-title eh-panel-title-flex'>
            <span>Documents</span>
            <button
              className='btn-outline eh-lic-docs-btn'
              type='button'
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <UploadFileOutlinedIcon style={{ fontSize: 16 }} />
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <input
              ref={fileRef}
              type='file'
              style={{ display: 'none' }}
              onChange={onUploadFile}
            />
          </div>
          {docs.length === 0 ? (
            <div className='eh-detail-note'>
              No documents yet — upload the issued license or supporting files.
            </div>
          ) : (
            <div className='eh-lic-doc-list'>
              {docs.map((d) => (
                <div className='eh-lic-doc-row' key={d.id}>
                  <DescriptionOutlinedIcon
                    style={{ fontSize: 16 }}
                    className='eh-lic-doc-icon'
                  />
                  <button
                    type='button'
                    className='eh-lic-doc-name'
                    onClick={() => downloadDoc(d)}
                    title='Download'
                  >
                    {d.file_name}
                  </button>
                  <button
                    type='button'
                    className='eh-lic-doc-dl'
                    onClick={() => downloadDoc(d)}
                    aria-label='Download'
                  >
                    <FileDownloadOutlinedIcon style={{ fontSize: 15 }} />
                  </button>
                  <button
                    type='button'
                    className='eh-lic-doc-x'
                    onClick={() => deleteDoc(d.id)}
                    aria-label='Remove'
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
            </div>
          </div>
        </div>
      </div>

      {/* Full-width commodity block below the two-column detail grid — the
          licence's line items carry eight columns of their own, which the
          right-hand column crushed. */}
      <div className='eh-lic-linked'>
        <div className='eh-lic-block-title eh-panel-title-flex'>
          <span>Linked Products ({linkedProducts.length})</span>
          <button
            className='eh-mini-btn'
            type='button'
            onClick={() => setLinkOpen(true)}
          >
            Link Product
          </button>
        </div>
        {linkedProducts.length === 0 && (
          <div className='eh-detail-note'>No products linked.</div>
        )}
        {linkedProducts.length > 0 && (
          /* The licence's commodity block (DSP-5 Blocks 9–13): one line per
             part with its own classification, quantity and unit value, and the
             line totals summed at the foot. */
          <div className='eh-lic-lines-scroll'>
            <table className='eh-records-table eh-lic-lines'>
              <thead>
                <tr>
                  <th>Part</th>
                  <th>USML Category</th>
                  <th>Commodity Code</th>
                  <th className='eh-dd-num'>Qty</th>
                  <th className='eh-dd-num'>Unit value</th>
                  <th className='eh-dd-num'>Line total</th>
                  <th className='eh-dd-num'>Drawn</th>
                  <th aria-label='Actions' />
                </tr>
              </thead>
              <tbody>
                {linkedProducts.map((p) => {
                  const roll = summary?.by_product.find(
                    (r) => r.product_id === p.id,
                  );
                  const drawn = roll?.drawn_value ?? 0;
                  const allocated = roll?.allocated_value ?? 0;
                  const drawnQty = roll?.drawn_quantity ?? 0;
                  const qty = p.allocated_quantity ?? null;
                  const unit = p.default_unit_value ?? null;
                  const qtyOver = qty !== null && qty > 0 && drawnQty > qty;
                  // The product's own recorded category. A line filed under a
                  // different one is legitimate, but worth surfacing — it
                  // usually means one of the two is stale.
                  const productCategory =
                    typeof p.jurisdiction_details?.usml_category === 'string'
                      ? p.jurisdiction_details.usml_category
                      : '';
                  const categoryConflict =
                    !!productCategory &&
                    !!p.line_usml_category &&
                    productCategory !== p.line_usml_category;
                  // The line total is the allocation. Qty x unit is what the
                  // form computes, but a licence is annotated against the
                  // figure actually filed, so allocated_value stays the truth.
                  const over = allocated > 0 && drawn > allocated;
                  return (
                    <tr key={p.id}>
                      <td>
                        <span className='eh-lic-line-name'>{p.name}</span>
                        <span className='eh-lic-line-sku'>
                          {p.internal_sku || 'No SKU'}
                        </span>
                      </td>
                      <td>
                        <StyledSingleSelect
                          options={[
                            { value: '', label: '—' },
                            ...USML_CATEGORIES.map((c) => ({
                              value: c,
                              label: c,
                            })),
                          ]}
                          value={p.line_usml_category || ''}
                          onChange={(v) =>
                            saveLine(p.id, { usml_category: v || null })
                          }
                          inline
                        />
                        {categoryConflict && (
                          <span
                            className='eh-lic-line-conflict'
                            title={`The product records ${productCategory}`}
                          >
                            <WarningAmberIcon style={{ fontSize: 12 }} />
                            Product records{' '}
                            {productCategory.split('—')[0].trim()}
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          className='eh-input eh-detail-edit'
                          value={
                            lineDraft[`${p.id}:code`] ?? (p.commodity_code || '')
                          }
                          placeholder='—'
                          onChange={(e) =>
                            setLineDraft((d) => ({
                              ...d,
                              [`${p.id}:code`]: e.target.value,
                            }))
                          }
                          onBlur={(e) => {
                            saveLine(p.id, {
                              commodity_code: e.target.value || null,
                            });
                            clearDraft(`${p.id}:code`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              (e.target as HTMLInputElement).blur();
                          }}
                          title='PM/DDTC commodity code'
                        />
                      </td>
                      <td className='eh-dd-num'>
                        <input
                          className='eh-input eh-detail-edit eh-eccn-input'
                          type='number'
                          min={0}
                          value={
                            lineDraft[`${p.id}:qty`] ??
                            (qty === null ? '' : String(qty))
                          }
                          placeholder='—'
                          onChange={(e) =>
                            setLineDraft((d) => ({
                              ...d,
                              [`${p.id}:qty`]: e.target.value,
                            }))
                          }
                          onBlur={(e) => {
                            saveLine(p.id, {
                              allocated_quantity:
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                            });
                            clearDraft(`${p.id}:qty`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              (e.target as HTMLInputElement).blur();
                          }}
                        />
                        {drawnQty !== 0 && (
                          <span
                            className={`eh-lic-line-sub${
                              qtyOver ? ' eh-lic-line-over' : ''
                            }`}
                            title={
                              qtyOver
                                ? 'Shipped quantity exceeds this line'
                                : undefined
                            }
                          >
                            {drawnQty} drawn
                          </span>
                        )}
                      </td>
                      <td className='eh-dd-num'>
                        <input
                          className='eh-input eh-detail-edit eh-eccn-input'
                          type='number'
                          min={0}
                          value={
                            lineDraft[`${p.id}:unit`] ??
                            (unit === null ? '' : String(unit))
                          }
                          placeholder='—'
                          onChange={(e) =>
                            setLineDraft((d) => ({
                              ...d,
                              [`${p.id}:unit`]: e.target.value,
                            }))
                          }
                          onBlur={(e) => {
                            saveLine(p.id, {
                              default_unit_value:
                                e.target.value === ''
                                  ? null
                                  : Number(e.target.value),
                            });
                            clearDraft(`${p.id}:unit`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              (e.target as HTMLInputElement).blur();
                          }}
                        />
                      </td>
                      <td className='eh-dd-num'>
                        <input
                          className='eh-input eh-detail-edit eh-eccn-input'
                          type='number'
                          min={0}
                          value={
                            lineDraft[`${p.id}:alloc`] ?? String(allocated || 0)
                          }
                          onChange={(e) =>
                            setLineDraft((d) => ({
                              ...d,
                              [`${p.id}:alloc`]: e.target.value,
                            }))
                          }
                          onBlur={(e) => {
                            saveLine(p.id, {
                              allocated_value: Number(e.target.value) || 0,
                            });
                            clearDraft(`${p.id}:alloc`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              (e.target as HTMLInputElement).blur();
                          }}
                          title='Line total authorized for this part'
                        />
                      </td>
                      <td
                        className={`eh-dd-num${over ? ' eh-lic-line-over' : ''}`}
                        title={over ? 'Drawn exceeds this line' : undefined}
                      >
                        {formatCurrency(drawn)}
                      </td>
                      <td className='eh-dd-actions'>
                        <div className='eh-dd-actions-inner'>
                          <button
                            className='eh-mini-btn'
                            type='button'
                            onClick={() => openRecord(p.id)}
                          >
                            Record
                          </button>
                          <button
                            className='eh-linked-x'
                            type='button'
                            onClick={() => unlinkProduct(p.id)}
                            title='Unlink product'
                            aria-label='Unlink product'
                          >
                            <CloseIcon style={{ fontSize: 14 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Totals (all lines)</td>
                  <td className='eh-dd-num'>
                    {linesQty === null ? '—' : linesQty}
                    {linesDrawnQty !== 0 && (
                      <span className='eh-lic-line-sub'>
                        {linesDrawnQty} drawn
                      </span>
                    )}
                  </td>
                  <td />
                  <td className='eh-dd-num'>{formatCurrency(linesTotal)}</td>
                  <td className='eh-dd-num'>{formatCurrency(linesDrawn)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Full-width ledger below the two-column detail grid. */}
      <DrawdownLedger
        groupId={groupId}
        drawdowns={drawdowns}
        summary={summary}
        loading={ddLoading}
        onRecord={() => openRecord()}
        onChanged={() => {
          loadDrawdowns();
          refreshLicense();
        }}
      />

      <LinkProductModal
        open={linkOpen}
        databaseId={license.database_id || ''}
        licenseId={license.id}
        groupId={groupId}
        linkedIds={linkedProducts.map((p) => p.id)}
        onClose={() => setLinkOpen(false)}
        onLinked={() => {
          setLinkOpen(false);
          loadLinkedProducts();
          loadDrawdowns();
          onPatched(license);
        }}
      />

      <RecordDrawdownModal
        open={recordOpen}
        groupId={groupId}
        licenses={recordLicenses}
        licenseId={license.id}
        lockedProductId={recordProductId}
        onClose={() => setRecordOpen(false)}
        onSaved={() => {
          setRecordOpen(false);
          loadDrawdowns();
          refreshLicense();
        }}
      />

      <div className='eh-lic-foot'>
        <div className='eh-lic-status-edit'>
          <span className='eh-field-label'>Status</span>
          <CreatableSelect
            value={license.status}
            onChange={(v) => v && changeStatus(v as ExportLicenseStatus)}
            options={LICENSE_STATUSES as unknown as string[]}
            allowCreate={false}
            disableClearable
            placeholder='Status'
            ariaLabel='Status'
          />
        </div>
        <button className='eh-lic-delete' type='button' onClick={remove}>
          <DeleteOutlineIcon style={{ fontSize: 16 }} /> Delete
        </button>
      </div>
    </div>
  );
};

const LicensesPanel: React.FC<Props> = ({
  databaseId,
  groupId,
  reloadKey,
  onChanged,
}) => {
  const [licenses, setLicenses] = useState<ExportLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeF, setTypeF] = useState<string[]>([]);
  const [agencyF, setAgencyF] = useState<string[]>([]);
  const [statusF, setStatusF] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [templates, setTemplates] = useState<ChecklistTemplate[]>([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!groupId) {
      setLicenses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = await ExportHubAPI.getLicenses(databaseId, groupId, {
        search: debouncedSearch || undefined,
        type: typeF.join(',') || undefined,
        agency: agencyF.join(',') || undefined,
        status: statusF.join(',') || undefined,
      });
      setLicenses(data);
    } catch (err) {
      console.error('Failed to load licenses:', err);
      setError(true);
      enqueueSnackbar('Failed to load licenses.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [databaseId, groupId, debouncedSearch, typeF, agencyF, statusF]);

  const refreshTemplates = useCallback(async () => {
    if (!groupId) return;
    try {
      setTemplates(await ExportHubAPI.getChecklistTemplates(groupId));
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  const patchLicense = (updated: ExportLicense) =>
    setLicenses((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
  const removeLicense = (id: string) => {
    setLicenses((prev) => prev.filter((l) => l.id !== id));
    onChanged?.();
  };

  const anyFilter =
    !!debouncedSearch || typeF.length > 0 || agencyF.length > 0 || statusF.length > 0;

  const toolbar = (
    <div className='eh-products-toolbar'>
      <div className='data-search-wrapper'>
        <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
        <input
          type='text'
          className='data-search-input'
          placeholder='Search license # or business unit'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className='eh-facet-row' style={{ flex: 1 }}>
        <FacetSelect
          label='Type'
          options={LICENSE_TYPES}
          selected={typeF}
          onChange={setTypeF}
        />
        <FacetSelect
          label='Agency'
          options={AGENCIES}
          selected={agencyF}
          onChange={setAgencyF}
        />
        <FacetSelect
          label='Status'
          options={LICENSE_STATUSES}
          selected={statusF}
          onChange={setStatusF}
        />
        <div className='eh-facet-spacer' />
        <button
          className='btn-outline'
          type='button'
          onClick={() => setManageOpen(true)}
        >
          <FactCheckOutlinedIcon style={{ fontSize: 16 }} /> Manage Templates
        </button>
        <button
          className='btn-primary'
          type='button'
          onClick={() => setRequestOpen(true)}
        >
          <AddIcon style={{ fontSize: 18 }} /> Add License
        </button>
      </div>
    </div>
  );

  const wizardAndDrawer = (
    <>
      <LicenseRequestWizard
        open={requestOpen}
        databaseId={databaseId}
        groupId={groupId}
        onClose={() => setRequestOpen(false)}
        onCreated={() => {
          setRequestOpen(false);
          load();
          onChanged?.();
        }}
      />
      <ManageTemplatesDrawer
        open={manageOpen}
        groupId={groupId}
        templates={templates}
        onClose={() => setManageOpen(false)}
        onChanged={refreshTemplates}
      />
    </>
  );

  if (loading) {
    return (
      <div className='export-hub-panel'>
        {toolbar}
        <div className='eh-table-skeleton'>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='eh-skel eh-skel-row' />
          ))}
        </div>
        {wizardAndDrawer}
      </div>
    );
  }

  if (error) {
    return (
      <div className='export-hub-panel'>
        {toolbar}
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>Couldn&rsquo;t load licenses</div>
          <button
            className='btn-outline'
            style={{ marginTop: 12 }}
            onClick={load}
            type='button'
          >
            Retry
          </button>
        </div>
        {wizardAndDrawer}
      </div>
    );
  }

  if (licenses.length === 0 && !anyFilter) {
    return (
      <div className='export-hub-panel'>
        <div className='export-hub-empty'>
          <div className='export-hub-empty-title'>No licenses yet</div>
          <div className='export-hub-empty-sub'>
            Add an export license to track its checklist, parties, and value.
          </div>
          <button
            className='btn-primary'
            style={{ marginTop: 16 }}
            onClick={() => setRequestOpen(true)}
            type='button'
          >
            <AddIcon style={{ fontSize: 18 }} /> Add License
          </button>
        </div>
        {wizardAndDrawer}
      </div>
    );
  }

  return (
    <div className='export-hub-panel'>
      {toolbar}
      <div className='runs-content-container'>
        <table className='runs-table'>
          <thead>
            <tr>
              <th>License #</th>
              <th>Type</th>
              <th>Business Unit</th>
              <th>Classification</th>
              <th>Effective Dates</th>
              <th>Value</th>
              <th>Agency</th>
              <th>Status</th>
              <th>Checklist</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map((l) => {
              const open = expandedId === l.id;
              return (
                <React.Fragment key={l.id}>
                  <tr
                    className='hoverable-row'
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(open ? null : l.id)}
                  >
                    <td>
                      <span className='eh-lens-name'>
                        {open ? (
                          <ExpandMoreIcon style={{ fontSize: 16 }} />
                        ) : (
                          <ChevronRightIcon style={{ fontSize: 16 }} />
                        )}
                        {l.license_number || (
                          <span className='eh-pending'>Pending</span>
                        )}
                      </span>
                    </td>
                    <td>{l.license_type}</td>
                    <td>{l.business_unit || '—'}</td>
                    <td>{classificationLabel(l)}</td>
                    <td>{dateRange(l)}</td>
                    <td>
                      {formatCurrency(
                        l.value !== null ? l.value : l.proposed_value,
                      )}
                    </td>
                    <td>{l.agency}</td>
                    <td>
                      <LicenseStatusPill value={l.status} />
                    </td>
                    <td>
                      {l.checklist.filter((c) => c.done).length}/
                      {l.checklist.length}
                    </td>
                  </tr>
                  {open && groupId && (
                    <tr className='eh-lens-detail-row'>
                      <td colSpan={9}>
                        <ExpandedLicense
                          license={l}
                          groupId={groupId}
                          templates={templates}
                          onPatched={patchLicense}
                          onDeleted={removeLicense}
                          onTemplatesChanged={refreshTemplates}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {licenses.length === 0 && (
              <tr>
                <td colSpan={9} className='eh-no-match'>
                  No licenses match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {wizardAndDrawer}
    </div>
  );
};

export default LicensesPanel;
